/**
 * nt-quote-drawer-v2.js
 * Drives the single global quote drawer (snippets/nt-quote-drawer-v2.liquid).
 * Any element with [data-nt-open-quote-drawer] opens it; an optional
 * [data-nt-quote-type] attribute sets the heading shown inside.
 * Name, email, phone and logo are required — validated per-field. The
 * logo/artwork picker accepts multiple files, uploads each one the instant
 * it's selected or dropped (not deferred to submit time), with per-file
 * progress/retry and individual or bulk removal. Submit posts the lead once
 * every file has finished uploading; a separate "prospect" partial-save
 * audit trail runs independently as the visitor fills the form — both via
 * window.NTQuoteAPI (assets/nt-quote-api.js).
 *
 * IMPORTANT: open/close must never depend on NTQuoteAPI — every trigger on
 * the site funnels through this file's click listener, so a thrown error
 * ahead of that binding (or inside open() itself) would silently break
 * every "Get a Quote" button. API-dependent behavior (validation, upload,
 * submit, prospect sync) is isolated and guarded so a missing/broken API
 * degrades the form instead of the drawer.
 */
(function () {
  "use strict";

  var API = window.NTQuoteAPI;
  var apiReady = !!API;
  if (!apiReady) {
    console.error(
      "nt-quote-drawer-v2.js: window.NTQuoteAPI is unavailable — quote form validation/submit is disabled, but the drawer will still open.",
    );
  }

  var drawer, backdrop, form, successView, errorBox, headingEl, tagEl;
  var currentCategory = "";
  var fileLabel, fileWrap, fileInput, fileHint, fileList, fileClearBtn;
  var DEFAULT_FILE_LABEL = "",
    DEFAULT_FILE_HINT = "";
  var releaseFocusTrap = null;
  var lastFocused = null;
  var dragStartY = null;
  var dragCurrentY = null;
  var prospect = null;
  var leadTracker = null;
  var prospectTimer = null;
  var fileManager = null;
  // Spam gate helpers live in assets/spam-gate.js (window.SpamGate). This
  // drawer has no sign-type or indoor/outdoor pickers to check (just
  // name/phone/email/logo), so the two picker-based conditions are
  // neutralized with an always-checked dummy checkbox — only scrollZero,
  // fastFill and noAds are meaningful signals here.
  var SPAM_DUMMY_ID = "nt-quote-drawer-v2-spam-dummy";
  var nameFieldEl, emailFieldEl;

  function q(sel) {
    return document.querySelector(sel);
  }

  function escName(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function ready() {
    drawer = q("[data-nt-quote-drawer]");
    if (!drawer) return;
    backdrop = q("[data-nt-quote-drawer-backdrop]");
    form = drawer.querySelector("[data-nt-quote-form]");
    successView = drawer.querySelector("[data-nt-quote-success]");
    errorBox = drawer.querySelector("[data-nt-quote-error]");
    headingEl = drawer.querySelector("[data-nt-quote-heading]");
    tagEl = drawer.querySelector("[data-nt-quote-tag]");
    fileLabel = drawer.querySelector("[data-nt-quote-file-label]");
    fileWrap = drawer.querySelector("[data-nt-quote-file-wrap]");
    fileInput = drawer.querySelector("[data-nt-quote-file]");
    fileHint = drawer.querySelector("[data-nt-quote-file-hint]");
    fileList = drawer.querySelector("[data-nt-quote-file-list]");
    fileClearBtn = drawer.querySelector("[data-nt-quote-file-clear]");
    DEFAULT_FILE_LABEL = fileLabel ? fileLabel.textContent : "";
    DEFAULT_FILE_HINT = fileHint ? fileHint.textContent : "";

    // ── Core open/close mechanics — no NTQuoteAPI dependency, ever. ──
    document.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-nt-open-quote-drawer]");
      if (trigger) {
        e.preventDefault();
        open(trigger.getAttribute("data-nt-quote-type"));
        return;
      }
      if (e.target.closest("[data-nt-quote-drawer-close]")) {
        e.preventDefault();
        close();
      }
    });

    if (backdrop) backdrop.addEventListener("click", close);

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
    });

    document.addEventListener("nt:request-quote-drawer", function (e) {
      open((e.detail || {}).type, (e.detail || {}).category);
    });

    var head = drawer.querySelector("[data-nt-quote-drag-handle]");
    if (head && window.matchMedia("(max-width: 640px)").matches) {
      head.addEventListener("touchstart", onTouchStart, { passive: true });
      head.addEventListener("touchmove", onTouchMove, { passive: true });
      head.addEventListener("touchend", onTouchEnd);
    }

    // ── Everything below touches window.NTQuoteAPI — guarded so a missing
    // or broken API degrades the form, not the drawer itself. ──
    if (!apiReady) {
      if (form) {
        form.addEventListener("submit", function (e) {
          e.preventDefault();
          showError(
            "This form is temporarily unavailable — please call us directly.",
          );
        });
      }
      return;
    }

    try {
      if (!document.getElementById(SPAM_DUMMY_ID)) {
        var spamDummyCb = document.createElement("input");
        spamDummyCb.type = "checkbox";
        spamDummyCb.id = SPAM_DUMMY_ID;
        spamDummyCb.checked = true;
        spamDummyCb.hidden = true;
        document.body.appendChild(spamDummyCb);
      }
      nameFieldEl = form.querySelector('[data-nt-quote-field="name"]');
      emailFieldEl = form.querySelector('[data-nt-quote-field="email"]');
      if (nameFieldEl && !nameFieldEl.id) nameFieldEl.id = "nt-quote-drawer-v2-spam-name";
      if (emailFieldEl && !emailFieldEl.id) emailFieldEl.id = "nt-quote-drawer-v2-spam-email";

      ["name", "phone", "email"].forEach(function (key) {
        var input = form.querySelector('[data-nt-quote-field="' + key + '"]');
        if (!input) return;
        input.addEventListener("input", function () {
          if (key === "phone" && API.validators.formatPhone) {
            input.value = API.validators.formatPhone(input.value);
          }
          if (input.classList.contains("has-error"))
            showFieldError(key, API.validators.fieldError(key, input.value));
          scheduleProspectSync();
        });
        input.addEventListener("blur", function () {
          showFieldError(key, API.validators.fieldError(key, input.value));
          syncProspect();
        });
      });
      var bodyField = form.querySelector('[name="contact[body]"]');
      if (bodyField) bodyField.addEventListener("blur", syncProspect);

      if (fileInput) {
        // Clear before the picker opens so choosing the same file again still
        // fires "change". Uploads use their own in-memory copy of each file
        // (NTQuoteAPI.createFileManager), so nothing depends on this input's
        // file list after selection — never rewrite it mid-upload.
        fileInput.addEventListener("click", function () {
          fileInput.value = "";
        });
        fileInput.addEventListener("change", function () {
          if (fileInput.files && fileInput.files.length)
            fileManager.addFiles(fileInput.files);
        });
        ["dragover", "dragenter"].forEach(function (ev) {
          fileWrap.addEventListener(ev, function (e) {
            e.preventDefault();
            fileWrap.classList.add("drag");
          });
        });
        ["dragleave", "drop"].forEach(function (ev) {
          fileWrap.addEventListener(ev, function () {
            fileWrap.classList.remove("drag");
          });
        });
        fileWrap.addEventListener("drop", function (e) {
          e.preventDefault();
          if (
            e.dataTransfer &&
            e.dataTransfer.files &&
            e.dataTransfer.files.length
          )
            fileManager.addFiles(e.dataTransfer.files);
        });
      }

      if (fileList) {
        fileList.addEventListener("click", function (e) {
          var retryBtn = e.target.closest("[data-retry-id]");
          if (retryBtn) {
            fileManager.retry(retryBtn.getAttribute("data-retry-id"));
            return;
          }
          var removeBtn = e.target.closest("[data-remove-id]");
          if (removeBtn)
            fileManager.remove(removeBtn.getAttribute("data-remove-id"));
        });
      }

      if (fileClearBtn) {
        fileClearBtn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          fileManager.clear();
        });
      }

      if (form) form.addEventListener("submit", handleSubmit);

      document.addEventListener("visibilitychange", function () {
        if (document.hidden && drawer.classList.contains("is-open"))
          syncProspect();
      });
      window.addEventListener("pagehide", function () {
        if (drawer.classList.contains("is-open")) syncProspect();
      });
    } catch (err) {
      console.error(
        "nt-quote-drawer-v2.js: form behavior failed to initialize, drawer open/close still works.",
        err,
      );
    }
  }

  function fieldValue(key) {
    var input = form.querySelector('[data-nt-quote-field="' + key + '"]');
    return input ? input.value.trim() : "";
  }

  function buildBasePayload() {
    var bodyField = form.querySelector('[name="contact[body]"]');
    return {
      category: currentCategory,
      name: fieldValue("name"),
      phone: fieldValue("phone"),
      email: fieldValue("email"),
      details: bodyField ? bodyField.value.trim() : "",
      signtypes: headingEl ? [headingEl.textContent.trim()] : [],
      lead_page_url: window.location.href,
      record_source: "Landing Page Categories",
    };
  }

  function syncProspect() {
    if (!prospect) return;
    var payload = buildBasePayload();
    payload.images = fileManager ? fileManager.urls() : [];
    prospect.sync(payload);
  }
  function scheduleProspectSync() {
    clearTimeout(prospectTimer);
    prospectTimer = setTimeout(syncProspect, 700);
  }

  function showFieldError(key, message) {
    var errEl = form.querySelector('[data-nt-quote-fielderr="' + key + '"]');
    var input = form.querySelector('[data-nt-quote-field="' + key + '"]');
    if (errEl) {
      errEl.textContent = message || "";
      errEl.hidden = !message;
    }
    if (input) input.classList.toggle("has-error", !!message);
  }

  function showFileError(message) {
    var errEl = form.querySelector('[data-nt-quote-fielderr="file"]');
    if (errEl) {
      errEl.textContent = message || "";
      errEl.hidden = !message;
    }
    if (fileWrap) fileWrap.classList.toggle("has-error", !!message);
  }

  function renderFileList(files) {
    if (!fileInput) return;
    if (!files.length) {
      if (fileWrap) fileWrap.classList.remove("has-file");
      if (fileLabel) fileLabel.textContent = DEFAULT_FILE_LABEL;
      if (fileHint) {
        fileHint.classList.remove("err");
        fileHint.textContent = DEFAULT_FILE_HINT;
      }
      if (fileClearBtn) fileClearBtn.hidden = true;
      if (fileList) fileList.innerHTML = "";
      showFileError("");
      return;
    }

    if (fileWrap) fileWrap.classList.add("has-file");
    if (fileLabel)
      fileLabel.textContent =
        files.length === 1
          ? files[0].file.name
          : files.length + " files selected";
    if (fileClearBtn) fileClearBtn.hidden = false;

    var hint = API.fileHint(files);
    if (fileHint) {
      fileHint.classList.toggle("err", hint.isError);
      fileHint.textContent = hint.text || DEFAULT_FILE_HINT;
    }

    if (fileList) {
      fileList.innerHTML = files
        .map(function (entry) {
          var st = API.entryStatus(entry);
          var statusClass = st.cls;
          var statusText = escName(st.text);
          var retryBtn = st.canRetry
            ? '<button type="button" class="nt-form-upload-item-retry" data-retry-id="' + entry.id + '">Retry</button>'
            : "";
          return (
            '<div class="nt-form-upload-item">' +
            '<span class="nt-form-upload-item-name">' +
            escName(entry.file.name) +
            "</span>" +
            '<span class="nt-form-upload-item-status ' +
            statusClass +
            '">' +
            statusText +
            "</span>" +
            retryBtn +
            '<button type="button" class="nt-form-upload-item-remove" data-remove-id="' +
            entry.id +
            '" aria-label="Remove file">' +
            '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
            "</button>" +
            "</div>"
          );
        })
        .join("");
    }

    showFileError("");
    scheduleProspectSync();
  }

  function createFileManagerFor(prospectTracker) {
    return API.createFileManager({
      name: function () {
        return fieldValue("name") || "lead";
      },
      uuid: prospectTracker.uuid,
      onChange: renderFileList,
    });
  }

  function open(type, category) {
    if (!drawer) return;
    currentCategory = category || "";
    lastFocused = document.activeElement;
    if (headingEl) headingEl.textContent = type || "Get a Free Quote";
    if (tagEl) tagEl.textContent = type ? "Quick quote" : "Free mockup & quote";
    resetForm();
    if (apiReady) {
      try {
        prospect = API.createProspectTracker();
        leadTracker = API.createLeadTracker(prospect.uuid);
        fileManager = createFileManagerFor(prospect);
      } catch (err) {
        console.error(
          "nt-quote-drawer-v2.js: createProspectTracker/createFileManager failed",
          err,
        );
      }
    }
    drawer.classList.add("is-open");
    if (backdrop) backdrop.classList.add("is-open");
    document.body.style.overflow = "hidden";
    if (window.ntUtils && window.ntUtils.trapFocus)
      releaseFocusTrap = window.ntUtils.trapFocus(drawer);
    var firstField = drawer.querySelector("input, textarea");
    if (firstField) firstField.focus();
    document.dispatchEvent(
      new CustomEvent("nt:quote-drawer-open", { detail: { type: type } }),
    );
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove("is-open");
    if (backdrop) backdrop.classList.remove("is-open");
    document.body.style.overflow = "";
    if (releaseFocusTrap) {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function resetForm() {
    if (form) {
      form.hidden = false;
      form.reset();
      form.querySelectorAll(".has-error").forEach(function (el) {
        el.classList.remove("has-error");
      });
      form.querySelectorAll("[data-nt-quote-fielderr]").forEach(function (el) {
        el.hidden = true;
        el.textContent = "";
      });
    }
    if (successView) successView.hidden = true;
    if (errorBox) errorBox.hidden = true;
    if (fileLabel) fileLabel.textContent = DEFAULT_FILE_LABEL;
    if (fileWrap) fileWrap.classList.remove("has-file", "has-error");
    if (fileHint) fileHint.textContent = DEFAULT_FILE_HINT;
    if (fileList) fileList.innerHTML = "";
    if (fileClearBtn) fileClearBtn.hidden = true;
  }

  function showError(message) {
    if (!errorBox) return;
    errorBox.textContent = message;
    errorBox.hidden = false;
  }

  function showThankYou() {
    if (successView) {
      try {
        var nameOut = successView.querySelector("[data-nt-quote-name]");
        var emailOut = successView.querySelector("[data-nt-quote-email]");
        var phoneOut = successView.querySelector("[data-nt-quote-phone]");
        var nameVal = fieldValue("name");
        var firstName = nameVal ? nameVal.split(/\s+/)[0] : "";
        if (nameOut) nameOut.textContent = firstName ? ", " + firstName : "";
        if (emailOut) emailOut.textContent = fieldValue("email");
        if (phoneOut) phoneOut.textContent = fieldValue("phone");
      } catch (e) { /* never let personalization break the thank-you screen */ }
    }
    form.hidden = true;
    if (successView) successView.hidden = false;
    try {
      history.pushState(null, "", window.location.pathname + "?thanks-for-response");
    } catch (e) { }
  }

  // A failed submit must NOT show the same screen as a real success — that
  // hid the fact that no lead was ever sent (e.g. logo upload never
  // completed), so nobody noticed the lead was missing. Keep the form
  // visible and surface a real error instead.
  function showSubmitError(err, errorContext) {
    try {
      if (typeof window.logClientError === "function") {
        window.logClientError(
          errorContext || "nt_quote_drawer_v2_submit",
          err || new Error("form submit failed"),
          prospect ? prospect.uuid : null,
        );
      }
    } catch (e) { /* never let the logger break the caller */ }
    var msg = err && err.message === "Logo upload did not complete"
      ? "Your logo failed to upload, so we couldn’t send your request. Please retry the upload above and submit again."
      : "Something went wrong submitting your request. Please try again.";
    if (errorBox) {
      errorBox.textContent = msg;
      errorBox.hidden = false;
      errorBox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // Marks the failed attempt in the URL (distinct from a real
    // ?thanks-for-response) without navigating away from the form or
    // showing the success view — kept for reporting/analytics that
    // differentiate the two outcomes.
    try {
      history.pushState(null, "", window.location.pathname + "?thanks-for-submission");
    } catch (e) { }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (errorBox) errorBox.hidden = true;

    var firstInvalid = null;
    ["name", "phone", "email"].forEach(function (key) {
      var input = form.querySelector('[data-nt-quote-field="' + key + '"]');
      var msg = API.validators.fieldError(key, input.value);
      showFieldError(key, msg);
      if (msg && !firstInvalid) firstInvalid = input;
    });

    var fileMsg =
      fileManager.hasLogo() || fileManager.isUploading()
        ? ""
        : "Please upload your logo so we can build your mockup.";
    showFileError(fileMsg);
    if (fileMsg && !firstInvalid) firstInvalid = fileInput;

    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    var submitBtn = form.querySelector('[type="submit"]');
    var origLabel = submitBtn ? submitBtn.textContent : "";

    var evaluation = window.SpamGate && window.SpamGate.evaluateSpam({
      signTypeSelector: "#" + SPAM_DUMMY_ID,
      placementIds: [SPAM_DUMMY_ID],
    });

    if (evaluation && evaluation.isSpam) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Sending…";
      }
      window.SpamGate.submitFormB(form, {
        spamReason: "ntqd2",
        emailFieldId: emailFieldEl ? emailFieldEl.id : "email",
        nameFieldId: nameFieldEl ? nameFieldEl.id : "name",
        onDone: function () {
          showThankYou();
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = origLabel;
          }
        },
      });
      return;
    }
    if (evaluation) window.SpamGate.attachDiagnosticMeta(form, evaluation, "ntqd2");
    // Mark end of this fill cycle so the next submit's elapsedMs is measured
    // from now, not from page load. Only formOpenedAt is reset; maxScroll persists.
    if (window.SpamGate) window.SpamGate.resetLeadFormState();

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending…";
    }

    // Fire the lead submit as soon as the logo upload is done — don't gate
    // it behind the prospect audit-trail chain (getChain()), which can still
    // have pending debounced autosave requests in flight and would otherwise
    // make /api/leads visibly wait behind those in the network tab.
    fileManager.waitForUploads()
      .then(function () {
        if (!fileManager.hasLogo())
          throw new Error("Logo upload did not complete");
        var payload = buildBasePayload();
        Object.assign(payload, fileManager.leadImageFields());
        return leadTracker.submit(payload);
      })
      .then(function (data) {
        prospect.finalize();
        showThankYou();
        var emailVal = fieldValue("email");
        // /api/leads (nt-quote-api.js) doesn't compute geo-IP server-side like the
        // legacy /api/no-approval endpoint did — it just echoes back whatever
        // `location` string the client sent. That string is set client-side in
        // layout/theme.liquid as "city, region, country, timezone, ip" (ip last),
        // and is already in localStorage before submit — read it from there.
        try {
          var location = (window.safeLocalStorage || localStorage).getItem("location") || (window.safeLocalStorage || localStorage).getItem("locationxh") || "";
          var locationParts = location ? location.split(", ") : [];
          var ipAddress = locationParts.length ? locationParts[locationParts.length - 1] : "";
          var spamIp = !!(ipAddress && (ipAddress.indexOf("2603:7000:b040:1ea1:") !== -1 || ipAddress.indexOf("174.255.203") !== -1));
          var spamEmail = !!(emailVal && emailVal.toLowerCase().indexOf("fastsigns") !== -1);
          if (!spamIp && !spamEmail) {
            if (typeof gtag_report_conversion === 'function') gtag_report_conversion();
          } else {
            console.log("gtag_report_conversion skipped for flagged lead:", { ipAddress: ipAddress, email: emailVal, spamIp: spamIp, spamEmail: spamEmail });
          }
        } catch (error) {
          console.error('Error during gtag_report_conversion:', error);
        }
        try {
          if (typeof bingTagFunction === "function") bingTagFunction(window, document, "script", "//bat.bing.com/bat.js", "uetq");
        } catch (e) {
          console.error("bingTagFunction error:", e);
        }
        try {
          if (typeof kalviyo === "function") kalviyo(emailVal);
        } catch (e) {
          console.error("kalviyo error:", e);
        }
        // /api/leads returns { data: { is_spam }, airtableRecordId } — the lead's
        // own Airtable record id, not the old { airtable_data: { body: { records }}} shape.
        try {
          var leadRecord = (data && data.data) || {};
          if (!leadRecord.is_spam && data && data.airtableRecordId && typeof checkisDuplicate === "function") {
            checkisDuplicate(data.airtableRecordId, emailVal, fieldValue("name"), "SignMakerz", fieldValue("phone"));
          }
        } catch (e) {
          console.error("checkisDuplicate error:", e);
        }
      })
      .catch(function (err) {
        showSubmitError(err, "nt_quote_drawer_v2_submit");
      })
      .finally(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = origLabel;
        }
      });
  }

  function onTouchStart(e) {
    dragStartY = e.touches[0].clientY;
    dragCurrentY = dragStartY;
    drawer.style.transition = "none";
  }
  function onTouchMove(e) {
    if (dragStartY === null) return;
    dragCurrentY = e.touches[0].clientY;
    var delta = Math.max(0, dragCurrentY - dragStartY);
    drawer.style.transform = "translateY(" + delta + "px)";
  }
  function onTouchEnd() {
    if (dragStartY === null) return;
    var delta = dragCurrentY - dragStartY;
    drawer.style.transition = "";
    drawer.style.transform = "";
    if (delta > 90) close();
    dragStartY = null;
    dragCurrentY = null;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready);
  } else {
    ready();
  }
})();
